import './styles.scss';

import { Button, Container } from 'react-bootstrap';
import { DtrParsedTrip, LocationMapping, PotentialLocation } from '@/models/gen/graphql';
import LocationReconciliationModal, {
  MissingLocationWithAirport,
  useLocationReconciliationModal,
} from '@/features/DriveTimeTool/components/LocationReconciliationModal';
import { convertDtrLocationToPotentialLocation, normalizeDtrLocation } from '@/features/DriveTimeTool/api/services';
import { createNotification, handleError, processBulkPromises } from '@/utils';
import {
  formatFullAddress,
  formatLocationKey,
  formatRoutekey,
  parseLocationSubKey,
  parseRouteKey,
} from '@/features/DriveTimeTool/utils/locations';
import { useNavigate, useParams } from 'react-router-dom';

import Feature from '@/common/Feature';
import { LoadingBlur } from '@/components/LoadingSpinner';
import LocationCard from '../../components/LocationCard';
import PageInfo from '@/components/PageInfo';
import { Toast } from '@/models';
import completeDtrManifestImport from '@/features/DriveTimeTool/api/services/completeDtrManifestImport';
import { createComponentState } from '@/state';
import getManifestParsedTrips from '@/features/DriveTimeTool/api/services/getManifestParsedTrips';
import { useEffect } from 'react';

export type DriveTimeToolRouteSelectionState = {
  importId: string;
  routes: Map<string, DtrParsedTrip[]>;
  locations: Map<string, PotentialLocation>; // grouped locations by airport key = formatted location sub-key `${name}::${airportCode}`
  missingLocations: Set<string>;
  trips: DtrParsedTrip[];
  selected: string[]; // routes keys split by "||"
  loading: boolean;
};
export const useDriveTimeToolRouteSelection = createComponentState<DriveTimeToolRouteSelectionState>({
  importId: undefined,
  routes: new Map(),
  locations: new Map(),
  missingLocations: new Set(),
  trips: [],
  selected: [],
  loading: false,
});

const DriveTimeToolRouteSelection = (): JSX.Element => {
  const { importId } = useParams<{ importId: string }>();
  const [state, setState] = useDriveTimeToolRouteSelection(({ state, setState }) => [state, setState]);
  const navigate = useNavigate();

  const getImportTrips = async (importId: string) => {
    try {
      if (!importId) throw new Error('No importId');
      setState((current) => ({ ...current, loading: true }));
      const res = await getManifestParsedTrips(importId);
      if (!res) return;
      // extract unique locations
      const locations = new Map<string, PotentialLocation>();
      (res.locations || []).forEach((location) => {
        if (locations.has(location.name)) return;
        locations.set(location.name, location);
      });
      // get routes from trip pu_location_id and doff_location_id  combinations are interchangeable
      const routes = new Map<string, DtrParsedTrip[]>();
      (res.trips || []).forEach((trip) => {
        const airport = trip.airportCode;
        const key = formatRoutekey(trip.puLocation, airport, trip.doLocation, airport);
        if (!routes.has(key)) routes.set(key, []);
        routes.get(key).push(trip);
      });
      // determine missing locations per-airport (composite sub-keys)
      const locationsByAirport = new Map<string, PotentialLocation>();
      const missingLocations = new Set<string>();
      Array.from(routes.keys()).forEach((routeKey) => {
        const [puSub, doSub] = parseRouteKey(routeKey);
        const puLoc = locations.get(puSub.name);
        const doLoc = locations.get(doSub.name);
        // check which locations are not resolved
        if (!puLoc.dtr_location_id) missingLocations.add(puSub.subKey);
        if (!doLoc.dtr_location_id) missingLocations.add(doSub.subKey);
        // store locations by composite sub-key to preserve airportCode
        if (!locationsByAirport.has(puSub.subKey)) locationsByAirport.set(puSub.subKey, { ...puLoc });
        if (!locationsByAirport.has(doSub.subKey)) locationsByAirport.set(doSub.subKey, { ...doLoc });
      });

      setState((current) => ({
        ...current,
        importId,
        trips: res.trips,
        routes,
        locations: locationsByAirport,
        missingLocations,
        selected: autoSelectRoutes(routes, missingLocations),
      }));
    } catch (err) {
      console.error('getImportTrips: failed to fetch trips:', err);
    } finally {
      setState((current) => ({ ...current, loading: false }));
    }
  };

  const handleSelectAllRoutes = () =>
    setState((current) => ({ ...current, selected: autoSelectRoutes(current.routes, current.missingLocations) }));

  const handleDeselectAllRoutes = () => setState((current) => ({ ...current, selected: [] }));

  const handleResolveLocation = (puLocation: PotentialLocation, doLocation: PotentialLocation, airportCode: string) => {
    const missing = getMissingLocationWithAirport(state.missingLocations, state.locations);
    if (puLocation?.dtr_location_id && doLocation?.dtr_location_id) return; // nothing to resolve
    if (!missing || missing.length === 0) return;
    // move the provided PU then DO location to the front of the list when they match by name + airport
    missing.sort((a, b) => {
      const aIsPu = puLocation && a.name === puLocation.name && a.airportCode === airportCode;
      const bIsPu = puLocation && b.name === puLocation.name && b.airportCode === airportCode;
      if (aIsPu && !bIsPu) return -1;
      if (!aIsPu && bIsPu) return 1;

      const aIsDo = doLocation && a.name === doLocation.name && a.airportCode === airportCode;
      const bIsDo = doLocation && b.name === doLocation.name && b.airportCode === airportCode;
      if (aIsDo && !bIsDo) return -1;
      if (!aIsDo && bIsDo) return 1;

      return 0;
    });

    useLocationReconciliationModal.getState().setState((current) => ({
      ...current,
      show: true,
      routes: state.routes,
      missingLocations: missing,
      currentLocationIndex: 0,
      address: formatFullAddress(missing[0]) || '',
    }));
  };

  const handleResolveAllLocations = () => {
    const missingLocations = getMissingLocationWithAirport(state.missingLocations, state.locations);
    useLocationReconciliationModal.getState().setState((current) => ({
      ...current,
      show: true,
      routes: state.routes,
      missingLocations: missingLocations,
      address: formatFullAddress(missingLocations[0]) || '',
      currentLocationIndex: 0,
    }));
  };

  const handleSubmitLocationReconciliation = async (resolvedLocations: MissingLocationWithAirport[]) => {
    try {
      if (resolvedLocations.length === 0) return;
      setState((current) => ({ ...current, loading: true }));
      await processBulkPromises(resolvedLocations, async (loc) => {
        const location = await normalizeDtrLocation(loc.address, loc.name, loc.airportCode);
        if (!location) return;
        setState((current) => {
          // remove created location from missingLocations using airportCode when provided
          const subKey = formatLocationKey(location.name, loc.airportCode);
          current.missingLocations.delete(subKey);
          return {
            ...current,
            selected: autoSelectRoutes(current.routes, current.missingLocations), // auto select routes that are now fully resolved
            locations: current.locations.set(subKey, convertDtrLocationToPotentialLocation(location)),
          };
        });
      });
    } catch (err) {
      console.error('DriveTimeToolRouteSelection: LocationReconciliationModal: onSubmit:', err?.message || err);
    } finally {
      setState((current) => ({ ...current, loading: false }));
    }
  };

  const handleCompleteImport = async () => {
    try {
      setState((current) => ({ ...current, loading: true }));
      if (!importId) throw new Error('No importId');
      if (!state.selected || state.selected.length === 0) throw new Error('No routes selected for import');
      // Only gather locations and trips that are part of the currently selected routes
      const locationKeys = new Set<string>();
      const tripIds: string[] = [];

      for (let i = 0; i < state.selected.length; i++) {
        const key = state.selected[i];
        if (!key) continue;
        const [puLocation, doLocation] = parseRouteKey(key);
        // keep composite sub-keys so we preserve airportCode when present
        locationKeys.add(puLocation.subKey);
        locationKeys.add(doLocation.subKey);

        const trips = state.routes.get(key);
        for (const trip of trips) tripIds.push(trip.id); // loop trips to get trip ids
      }

      const locationMapping: LocationMapping[] = Array.from(locationKeys).map((subKey): LocationMapping => {
        const loc = state.locations.get(subKey);
        return {
          dtrLocationId: loc?.dtr_location_id,
          dtrLocationName: loc?.name,
        };
      });

      const res = await completeDtrManifestImport(importId, locationMapping, tripIds);
      if (!res?.manifestHistoryId) throw new Error('No manifestHistoryId returned from completeDtrManifestImport');
      createNotification(`We're generating your drive time report.`, Toast.Type.SUCCESS, 'Processing Drive Time Report...');
      navigate(`/tools/drive-time?importId=${res.manifestHistoryId}`);
    } catch (err) {
      console.error('handleCompleteImport:', err?.message || err);
      handleError(err, { notification: { title: 'Completing Import' } });
    } finally {
      setState((current) => ({ ...current, loading: false }));
    }
  };

  useEffect(() => {
    if (!importId)
      return () => {
        setState((current) => ({
          ...current,
          importId: undefined,
          routes: new Map(),
          locations: new Map(),
          missingLocations: new Set(),
          trips: [],
          selected: [],
          loading: false,
        }));
      };
    getImportTrips(importId);
  }, [importId]);

  return (
    <Feature name="DriveTimeToolRouteSelection">
      <Container fluid>
        <LoadingBlur loading={state.loading} />
        <PageInfo>Routes Selected: {state.selected.length}</PageInfo>
        <div className="RouteSelectionHeader py-4 border-bottom border-gray-subtle d-flex justify-content-between align-items-center">
          <div className="d-flex">
            <Button variant="icon" onClick={() => navigate(-1)} className="me-5 d-flex align-items-center justify-content-center">
              <i className="sv sv-arrow-left fs-3 me-2" />
              Back To Upload
            </Button>
            <div className="d-flex align-items-center">
              <span className="p-3 icon-container shadow rounded-3 d-flex justify-content-center align-items-center me-3 bg-purple-gradient">
                <i className="sv sv-map fs-1 text-white" />
              </span>
              <div className="d-flex flex-column justify-content-center flex-grow-1">
                <h3>ROUTE SELECTION</h3>
                <p className="m-0 text-muted">Review and select routes to import</p>
              </div>
            </div>
          </div>
          <div className="d-flex flex-nowrap gap-3">
            <span className="rounded-pill border border-1 border-cyan text-primary py-2 px-3 bg-cyan-subtle d-flex align-items-center">
              <i className="sv sv-document fs-4 me-2" />
              {state.trips.length} Trips
            </span>
            <span className="rounded-pill border border-1 border-success text-success py-2 px-3 bg-success-subtle d-flex align-items-center">
              <i className="sv sv-map fs-4 me-2" />
              {state.routes.size} Routes
            </span>
          </div>
        </div>
        <div className="RouteSelectionActions d-flex justify-content-between">
          <div className="d-flex gap-3 flex-no-wrap align-items-center">
            <Button variant="gray-subtle" className="d-flex align-items-center btn-shadow" onClick={handleSelectAllRoutes}>
              <i className="sv sv-checkmark-circle fs-4 me-2" />
              Select All
            </Button>
            <Button variant="gray-subtle" className="d-flex align-items-center btn-shadow" onClick={handleDeselectAllRoutes}>
              <i className="sv sv-checkmark-circle fs-4 me-2" />
              Deselect All
            </Button>
            {state.missingLocations.size > 0 && (
              <p className="m-0 d-flex align-items-center fw-semibold">
                <span className="text-danger">{state.missingLocations.size} Locations Need Reconciliation</span>
                <i className="sv sv-arrow-right mx-3" />
                <span
                  role="button"
                  className="link-success text-decoration-underline link-offset-2 link-underline-opacity-25 link-underline-opacity-100-hover pointer"
                  onClick={handleResolveAllLocations}
                >
                  Resolve All
                </span>
              </p>
            )}
          </div>
          <Button variant="primary" className="d-flex align-items-center btn-shadow" onClick={handleCompleteImport}>
            <i className="sv sv-upload2 fs-4 me-2" />
            Complete Import
          </Button>
        </div>
        <div className="TripSelectionContent flex-grow-1 d-flex flex-column justify-content-center gap-5">
          {!state.routes.size && <h3 className="text-center text-muted">No routes found</h3>}
          {Array.from(state.routes.entries()).map(([key, trips], index) => {
            const [puSub, doSub] = parseRouteKey(key);
            const puLocation = state.locations.get(puSub.subKey);
            const doLocation = state.locations.get(doSub.subKey);
            return (
              <LocationCard
                key={index}
                puLocation={puLocation}
                doLocation={doLocation}
                trips={trips}
                selected={state.selected.includes(key)}
                airportCode={puSub.airportCode}
                onResolveLocations={() => handleResolveLocation(puLocation, doLocation, puSub.airportCode)}
              />
            );
          })}
        </div>
        <div className="DriveTimeTool-Footer text-center pt-4 pb-5">
          <span className="text-muted">Powered by SkyHop Technologies</span>
        </div>
      </Container>
      <LocationReconciliationModal onSubmit={handleSubmitLocationReconciliation} />
    </Feature>
  );
};

// select all routes that don't have missing locations
const autoSelectRoutes = (routes: Map<string, DtrParsedTrip[]>, missingLocations: Set<string>): string[] =>
  Array.from(routes.keys()).filter((key) => {
    const [puSub, doSub] = parseRouteKey(key);
    return !missingLocations.has(puSub.subKey) && !missingLocations.has(doSub.subKey);
  });

// converts missingLocations set of composite sub-keys into array of MissingLocationWithAirport
const getMissingLocationWithAirport = (
  missingLocations: Set<string>,
  locations: Map<string, PotentialLocation>
): MissingLocationWithAirport[] =>
  Array.from(missingLocations).map((subKey) => {
    const { airportCode } = parseLocationSubKey(subKey);
    const loc = locations.get(subKey);
    return { ...loc, airportCode };
  });

export default DriveTimeToolRouteSelection;
